import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label } from '@/components/ui'
import { Lock, Search, Copy, Check, ThumbsUp, ThumbsDown, RefreshCw, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useVotes } from '@/hooks/useVotes'

interface BinInfo {
  BIN: string
  Brand: string
  Type: string
  Category: string
  Issuer: string
  IssuerPhone: string
  IssuerUrl: string
  isoCode2: string
  isoCode3: string
  CountryName: string
  CountryFlag: string
  Currency: string
  ProductId: string
  ProductType: string
  RegulatedIndicator: string
  BinMax: string
}

export function Binlist() {
  const [bin, setBin] = useState('')
  const [result, setResult] = useState<BinInfo | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [reportSite, setReportSite] = useState('')
  const { votes, castVote } = useVotes('binlist')

  const lookupBin = async () => {
    if (!bin || bin.length < 6) {
      toast.error('Please enter at least 6 digits')
      return
    }

    setLoading(true)
    setResult(null) // Clear previous result
    try {
      const response = await fetch(`https://asianprozyy.us/bin/${bin.trim()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: BinInfo = await response.json()
      setResult(data)
      toast.success('BIN lookup complete')
    } catch (error) {
      toast.error('Failed to lookup BIN')
      console.error('BIN lookup error:', error)
    } finally {
      setLoading(false)
    }
  }

  const copyResult = async () => {
    if (!result) return
    try {
      await navigator.clipboard.writeText(JSON.stringify(result, null, 2))
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const submitReport = () => {
    if (!reportSite.trim()) {
      toast.error('Please provide a site URL')
      return
    }
    castVote('needs_update')
    toast.success('Report submitted. We will check and update soon.')
    setShowReport(false)
    setReportSite('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20">
              <Lock className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">BINLIST</h1>
              <p className="text-muted-foreground">BIN/IIN lookup tool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => castVote('working')}>
              <ThumbsUp className="h-4 w-4 mr-2 text-green-500" />
              {votes.working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => castVote('not_working')}>
              <ThumbsDown className="h-4 w-4 mr-2 text-red-500" />
              {votes.not_working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowReport(true)}>
              <RefreshCw className="h-4 w-4 mr-2 text-yellow-500" />
              {votes.needs_update}
            </Button>
          </div>
        </div>
      </div>

      {showReport && (
        <Card className="glass border-yellow-500/50">
          <CardContent className="pt-4">
            <div className="space-y-3">
              <p className="text-sm text-yellow-500">Please provide the site URL so we can check and update:</p>
              <Input
                value={reportSite}
                onChange={(e) => setReportSite(e.target.value)}
                placeholder="https://example.com"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitReport}>Submit</Button>
                <Button size="sm" variant="outline" onClick={() => setShowReport(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Lookup</CardTitle>
            <CardDescription>Enter BIN to lookup card information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>BIN (First 6-8 digits)</Label>
              <div className="flex gap-2">
                <Input
                  value={bin}
                  onChange={(e) => setBin(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="Enter BIN (e.g., 414720)"
                  className="font-mono"
                />
                <Button onClick={lookupBin} disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Lookup
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Result</CardTitle>
                <CardDescription>Card information</CardDescription>
              </div>
              {result && (
                <Button variant="outline" size="sm" onClick={copyResult}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {result ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">BIN</p>
                    <p className="font-medium">{result.BIN || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Brand</p>
                    <p className="font-medium">{result.Brand || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Type</p>
                    <p className="font-medium">{result.Type || 'N/A'}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Category</p>
                    <p className="font-medium">{result.Category || 'N/A'}</p>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Issuer</p>
                    <p className="font-medium">{result.Issuer || 'N/A'}</p>
                    {result.IssuerUrl && <p className="text-xs text-muted-foreground">{result.IssuerUrl}</p>}
                    {result.IssuerPhone && <p className="text-xs text-muted-foreground">{result.IssuerPhone}</p>}
                </div>
                <div className="p-3 rounded-lg bg-muted/30">
                    <p className="text-xs text-muted-foreground">Country</p>
                    <p className="font-medium">{result.CountryFlag} {result.CountryName} ({result.isoCode2})</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground">Currency</p>
                        <p className="font-medium">{result.Currency || 'N/A'}</p>
                    </div>
                    <div className="p-3 rounded-lg bg-muted/30">
                        <p className="text-xs text-muted-foreground">Prepaid</p>
                        <p className="font-medium">{result.Type === 'PREPAID' ? 'Yes' : 'No'}</p>
                    </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>Enter a BIN to lookup card information</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Card className="glass">
        <CardHeader>
          <CardTitle>Request an Encryptor</CardTitle>
          <CardDescription>
            Can't find the encryptor you're looking for? Request it here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/request-encryptor">
            <Button>Request Encryptor</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}